import React from "react";

import {
  Form,
  Formik,
  getIn,
  useField,
  useFormikContext,
  yupToFormErrors,
} from "formik";
import Grid from "@material-ui/core/Grid";

import { Input, Switch, withNamespace } from "components/Form/FormikControls";

import { emailRecipientFormValidation } from "components/Business/BusinessForm/EmailRecipients/validation";
import { Warning } from "components/ErrorMessage";
import { Button, SecondaryButton } from "components/Form";
import { AccountsRecipientIcon } from "components/Icons/AccountsRecipientIcon";
import { CommercialEmailRecipientIcon } from "components/Icons/CommercialEmailRecipientIcon";
import { ComplianceEmailRecipientIcon } from "components/Icons/ComplianceEmailRecipientIcon";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/MaterialDialog";
import { ConfirmDialog } from "components/ConfirmDialog";
import { useBoolean } from "hooks/useBoolean";
import { AuditLogTypes } from "constants/auditLog";
import AuditLogLink from "components/AuditLog/AuditLogLink";

import { getIsLivestockAgent } from "selectors";
import { useSelector } from "react-redux";

interface EmailRecipientFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  isEditingExisting: boolean;
  isAuditableRecipient: boolean;
  auditLogType?: string;
  editingEmailRecipientId?: number;
  action: string;
}

function EmailRecipientFormModal({
  isOpen,
  onClose,
  isEditingExisting,
  isAuditableRecipient,
  auditLogType,
  editingEmailRecipientId,
  action,
}: EmailRecipientFormModalProps): React.JSX.Element {
  const [isConfirmCloseOpen, openConfirmClose, closeConfirmClose] =
    useBoolean(false);

  const { dirty, errors, isValid, touched } = useFormikContext<{
    noInputs?: string;
  }>();

  const hasTouchedFields = Object.values(touched).filter(Boolean).length > 0;

  const doHandleClose = (): void => {
    if (dirty || hasTouchedFields) {
      openConfirmClose();
    } else {
      onClose();
    }
  };

  const handleConfirmDiscard = (): void => {
    closeConfirmClose();
    onClose();
  };

  const submitEnabled = (dirty || hasTouchedFields) && isValid;

  const id = useField("id")[0].value;

  return (
    <>
      <Dialog open={isOpen} onClose={doHandleClose}>
        <DialogTitle onClose={doHandleClose}>
          {isAuditableRecipient ? (
            <>
              <AuditLogLink
                auditLogType={auditLogType}
                dataId={editingEmailRecipientId}
                returnTo={window.location.hash}
              />
              &nbsp;{action}
            </>
          ) : (
            action
          )}{" "}
          Contact
        </DialogTitle>
        <Form data-tour="emailRecipientForm">
          <DialogContent>
            <Grid container spacing={2}>
              <input name="id" type="hidden" value={id || undefined} />

              <Grid item xs={12} sm={6}>
                <Input label="First Name" name="firstName" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input label="Last Name" name="lastName" />
              </Grid>

              <Grid item xs={12} md={6}>
                <Input label="Email" name="email" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Input label="Phone" name="phoneNumber" />
              </Grid>

              {errors.noInputs && (
                <Grid item xs={12} sm={12} className="p-2">
                  <Warning>{errors.noInputs}</Warning>
                </Grid>
              )}

              <Grid item xs={12} sm={6}>
                <Switch
                  label={
                    <>
                      Is Accounts Recipient&nbsp;
                      <AccountsRecipientIcon color="primary" fontSize="delta" />
                    </>
                  }
                  name="isAccountsRecipient"
                  tooltip="Contacts receive documents generated through the Billing process. (Eg Invoice, RCTI, etc)"
                  alignCenter
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Switch
                  label={
                    <>
                      Is Commercial Recipient&nbsp;
                      <CommercialEmailRecipientIcon
                        color="primary"
                        fontSize="delta"
                      />
                    </>
                  }
                  name="isCommercialReportRecipient"
                  tooltip="Contacts receive price sensitive saleyard reports. (Eg Buyer Reconciliations, Purchaser Details, etc)"
                  alignCenter
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Switch
                  label={
                    <>
                      Is Compliance Recipient&nbsp;
                      <ComplianceEmailRecipientIcon
                        color="primary"
                        fontSize="delta"
                      />
                    </>
                  }
                  name="isComplianceReportRecipient"
                  tooltip="Contacts receive non-price sensitive saleyard reports. (Eg Post Sale Summary, RFID Reports, etc)"
                  alignCenter
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <SecondaryButton onClick={onClose} type="button">
              Cancel
            </SecondaryButton>
            <Button
              data-tour="emailModalSubmit"
              disabled={!submitEnabled}
              type="submit"
            >
              {isEditingExisting ? "Update" : "Add"} Contact
            </Button>
          </DialogActions>
        </Form>
      </Dialog>
      <ConfirmDialog
        title="Are you sure you want to cancel?"
        message="You will lose these changes."
        isOpen={isConfirmCloseOpen}
        onCancel={closeConfirmClose}
        onDelete={handleConfirmDiscard}
        buttonMessage="Confirm"
        cancelMessage="Back"
      />
    </>
  );
}

interface EmailRecipientFormSectionProps {
  namespace: string;
  onSetPrimaryContactEmail: (email: string) => void;
}

export function EmailRecipientFormSection({
  namespace: ns,
  onSetPrimaryContactEmail,
}: EmailRecipientFormSectionProps) {
  const formikContext = useFormikContext();
  const { values, setFieldValue, setFieldTouched } = formikContext;

  const emailRecipients = getIn(values, ns) as EmailRecipient[] | undefined;

  const isLivestockAgent = useSelector(getIsLivestockAgent);
  const auditLogType = isLivestockAgent
    ? AuditLogTypes.DEPLOYMENT_BUSINESS_EMAIL_RECIPIENT
    : AuditLogTypes.SALEYARD_BUSINESS_EMAIL_RECIPIENT;

  const editingEmailRecipient = getIn(
    values,
    withNamespace(ns, "editingValue"),
  ) as EmailRecipient | null | undefined;

  // Existing EmailRecipients have an Id of positive number (the database id).
  // Temporary or pending EmailRecipients have a negative Id (-Date.now()),
  //  so that they can be further tracked and edited before the form is submitted.
  // Creates (new on this open) default to 0, and are then assigned a negative Id
  //  see handleSubmit) when the EmailRecipientForm form is submitted.
  const editingEmailRecipientId: number = editingEmailRecipient?.id || 0;

  const isOpen = Boolean(editingEmailRecipient);

  const emailRecipientsCount = emailRecipients?.length || 0;

  function handleClose(): void {
    setFieldValue(withNamespace(ns, "editingValue"), null, true);
  }

  const handleSubmit = (formValues: EmailRecipient) => {
    if (!emailRecipients || !formValues) return;
    const newState = emailRecipients.slice();
    const existingIndex = newState.findIndex(
      (deploymentEmailRecipient: EmailRecipient) =>
        deploymentEmailRecipient.id === formValues.id,
    );
    if (existingIndex === -1) {
      // When this is a new DeploymentEmailRecipient
      newState.splice(newState.length, 1, {
        ...formValues,
        // Add a temporary (offline) id to allow the front end to track the changes to this entry.
        // Once submitted, and a response is returned, this will have and actual database primary key in here.
        id: -Date.now(),
      });

      if (emailRecipientsCount === 0 && formValues.email) {
        // adding the first contact - make it primary
        onSetPrimaryContactEmail(formValues.email);
      }
    } else {
      // When the user is editing an existing EmailRecipient
      newState.splice(existingIndex, 1, formValues);

      if (emailRecipientsCount === 1 && formValues.email) {
        // edited the only contact - make it primary
        onSetPrimaryContactEmail(formValues.email);
      }
    }

    setFieldValue(ns, newState, false);
    setFieldValue(withNamespace(ns, "editingValue"), null, true);

    setFieldTouched(ns, true);
  };

  const isEditingExisting =
    editingEmailRecipient &&
    Array.isArray(emailRecipients) &&
    emailRecipients.some(
      (emailRecipient: EmailRecipient) =>
        emailRecipient.id === editingEmailRecipientId,
    );

  const action = isEditingExisting ? "Update" : "Add";

  const isAuditableRecipient = isEditingExisting && editingEmailRecipientId > 0;

  // set touched, to allow immediate submit, when initialized with field values
  // and not editing an existing contact (has a positive ID), i.e., when adding a suggested contact
  const initialTouched = editingEmailRecipientId <= 0 && {
    firstName: Boolean(editingEmailRecipient?.firstName),
    lastName: Boolean(editingEmailRecipient?.lastName),
    email: Boolean(editingEmailRecipient?.email),
    phoneNumber: Boolean(editingEmailRecipient?.phoneNumber),
  };

  // pass extra context to Yup validation
  const validate = async (formValues: EmailRecipient) => {
    try {
      await emailRecipientFormValidation.validate(formValues, {
        abortEarly: false,
        context: {
          emailRecipients,
          editingEmailRecipient,
        },
      });

      return {};
    } catch (error) {
      return yupToFormErrors(error);
    }
  };

  return (
    <Formik
      enableReinitialize
      initialTouched={initialTouched}
      initialValues={editingEmailRecipient || {}}
      validate={validate}
      onSubmit={handleSubmit}
      validateOnMount
    >
      <EmailRecipientFormModal
        isOpen={isOpen}
        onClose={handleClose}
        isEditingExisting={Boolean(isEditingExisting)}
        isAuditableRecipient={Boolean(isAuditableRecipient)}
        auditLogType={auditLogType}
        editingEmailRecipientId={editingEmailRecipientId}
        action={action}
      />
    </Formik>
  );
}
